'use client'

import { useLocale, useTranslations } from 'next-intl'
import Link from 'next/link'
import { useState, useRef, useEffect } from 'react'
import useSWRInfinite from 'swr/infinite'
import { ChevronLeft, ChevronRight } from 'lucide-react'

import type { Template, User } from '@/lib/types'
import { cn, fetcher } from '@/lib/utils'
import { LoadMoreBtn } from '../load-more-btn'
import { CATEGORIES } from '@/lib/constant'
import { OptimizedTemplateImage } from './optimized-template-image'

const PAGE_SIZE = 20

const TEMPLATE_CATEGORIES = [
  { id: 'all', label: 'All' },
  ...CATEGORIES,
] as const

const getKeyBuilder =
  (category: string) =>
  (page: number, previousPageData: { data: Template[] } | null) => {
    if (previousPageData && !previousPageData.data.length) return null
    const limit = PAGE_SIZE
    const skip = page * limit
    const categoryParam =
      category === 'all' ? '' : `&category=${encodeURIComponent(category)}`
    return `/api/templates?skip=${skip}&limit=${limit}${categoryParam}`
  }

// Custom fetcher with localStorage cache
const templateFetcher = async (url: string) => {
  // Check localStorage cache first
  if (typeof window !== 'undefined') {
    const cacheKey = `template_cache_${url}`
    const cached = localStorage.getItem(cacheKey)

    if (cached) {
      try {
        const { data, timestamp } = JSON.parse(cached)
        const CACHE_DURATION = 24 * 60 * 60 * 1000 // 24 hours
        const now = Date.now()

        // If cache is still valid, return cached data
        if (now - timestamp < CACHE_DURATION) {
          console.log('📦 Using cached template data for:', url)
          return data
        } else {
          // Cache expired, remove it
          localStorage.removeItem(cacheKey)
        }
      } catch (e) {
        // Invalid cache, remove it
        localStorage.removeItem(cacheKey)
      }
    }
  }

  // Fetch from API
  const response = await fetcher(url)

  // Save to localStorage cache
  if (typeof window !== 'undefined' && response) {
    const cacheKey = `template_cache_${url}`
    try {
      localStorage.setItem(
        cacheKey,
        JSON.stringify({
          data: response,
          timestamp: Date.now(),
        }),
      )
    } catch (e) {
      // localStorage might be full, ignore error
      console.warn('Failed to cache template data:', e)
    }
  }

  return response
}

export const TemplateList = ({
  user,
  onLoadingChange,
}: {
  user: User | null
  onLoadingChange?: (loading: boolean) => void
}) => {
  const [activeCategory, setActiveCategory] = useState('all')
  const [isCategoryLoading, setIsCategoryLoading] = useState(false)

  const handleCategoryChange = (category: string) => {
    if (category === activeCategory) return
    setIsCategoryLoading(true)
    onLoadingChange?.(true)
    setActiveCategory(category)
  }

  return (
    <div className={cn('rounded-2xl relative')}>
      <TemplateCategories
        activeCategory={activeCategory}
        setActiveCategory={handleCategoryChange}
      />
      <div className="relative min-h-[400px]">
        <TemplateListData
          key={activeCategory}
          user={user}
          category={activeCategory}
          onLoadingChange={(loading) => {
            setIsCategoryLoading(loading)
            onLoadingChange?.(loading)
          }}
        />
      </div>
    </div>
  )
}

const TemplateCategories = ({
  activeCategory,
  setActiveCategory,
}: {
  activeCategory: string
  setActiveCategory: (category: string) => void
}) => {
  const t = useTranslations('templates.categories')
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current
      const scrollAmount = container.clientWidth * 0.8
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      })
    }
  }

  return (
    <div className="flex items-center gap-2 mb-8 z-10 relative">
      <button
        type="button"
        onClick={() => scroll('left')}
        className="flex-shrink-0 text-white/60 hover:text-white transition-colors duration-200 -ml-4 p-1"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <div
        ref={scrollContainerRef}
        className="flex gap-2 overflow-x-auto flex-1 no-scrollbar"
      >
        {TEMPLATE_CATEGORIES.map((category) => (
          <button
            type="button"
            key={category.id}
            onClick={() => setActiveCategory(category.id)}
            className={cn(
              'px-3 md:px-4 py-2 rounded-lg text-sm transition-all whitespace-nowrap flex-shrink-0 hover:bg-black/40',
              activeCategory === category.id
                ? 'font-medium bg-black/40 text-white border border-white/10'
                : 'bg-black/30 text-white/60',
            )}
          >
            {t(category.id)}
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={() => scroll('right')}
        className="flex-shrink-0 text-white/60 hover:text-white transition-colors duration-200 -mr-4 p-1"
      >
        <ChevronRight className="w-5 h-5" />
      </button>
    </div>
  )
}

const TemplateListSkeleton = () => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 min-[1800px]:grid-cols-5 gap-4">
      {Array.from({ length: 8 }).map((_, index) => (
        <div key={index} className="w-full text-left h-full flex">
          <div className="rounded-xl transition-all duration-200 bg-gradient-to-br from-white/5 via-white/5 to-white/[0.02] backdrop-blur-md overflow-hidden shadow-lg shadow-black/20 flex flex-col w-full h-[240px] relative animate-pulse">
            {/* Shimmer effect */}
            <div className="absolute inset-0 overflow-hidden rounded-xl">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-[shimmer_2s_infinite]" />
            </div>

            <div className="relative flex-shrink-0">
              <div className="w-full aspect-video rounded-t-lg overflow-hidden bg-transparent flex items-center justify-center">
                {/* Pulsing dots for image area */}
                <div className="absolute top-2 right-2 flex gap-1">
                  <div
                    className="w-1 h-1 bg-gold/60 rounded-full animate-pulse"
                    style={{ animationDelay: '0ms' }}
                  />
                  <div
                    className="w-1 h-1 bg-gold/60 rounded-full animate-pulse"
                    style={{ animationDelay: '150ms' }}
                  />
                  <div
                    className="w-1 h-1 bg-gold/60 rounded-full animate-pulse"
                    style={{ animationDelay: '300ms' }}
                  />
                </div>
              </div>
            </div>
            <div className="px-3 py-2 flex flex-col justify-center min-h-[48px]">
              <div
                className="h-3 bg-white/10 rounded w-3/4 mx-auto animate-pulse"
                style={{ animationDelay: '450ms' }}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

const TemplateListData = ({
  category,
  onLoadingChange,
}: {
  user: User | null
  category: string
  onLoadingChange?: (loading: boolean) => void
}) => {
  const { data, error, isLoading, setSize, size, isValidating } =
    useSWRInfinite<{
      data: Template[]
      total: number
    }>(getKeyBuilder(category), templateFetcher, {
      errorRetryCount: 3,
      errorRetryInterval: 1000,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
      dedupingInterval: 24 * 60 * 60 * 1000, // Cache for 24 hours to avoid duplicate requests
      keepPreviousData: true, // Keep previous data while loading new data
    })
  const t = useTranslations('templates.categories')

  // Update loading state when data loading changes
  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(isLoading && !data)
    }
  }, [isLoading, data, onLoadingChange])

  if (error)
    return (
      <div className="text-center py-10">
        <div className="text-white/60">{t('error')}</div>
      </div>
    )
  if (isLoading || !data) {
    return <TemplateListSkeleton />
  }

  const templates = data.flatMap((page) => page.data)
  const isEmpty = data?.[0]?.data?.length === 0
  const isReachingEnd =
    isEmpty || (data && data[data.length - 1]?.data.length < PAGE_SIZE)

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 min-[1800px]:grid-cols-5 gap-4">
        {templates.map((template) => (
          <TemplateCard key={template._id.toString()} template={template} />
        ))}
      </div>

      {!isReachingEnd && (
        <div className="my-8 flex justify-center relative">
          <LoadMoreBtn
            onClick={() => {
              console.log('Load More clicked, current size:', size)
              setSize((prev) => {
                console.log('Setting size from', prev, 'to', prev + 1)
                return prev + 1
              })
            }}
            disabled={isValidating}
          />
        </div>
      )}
    </>
  )
}

const TemplateCard = ({
  template,
}: {
  template: Template
  transparent?: boolean
}) => {
  const locale = useLocale()
  return (
    <Link
      href={`/template/${template.slug || template._id}`}
      target="_blank"
      rel="noopener noreferrer"
      className="w-full text-left h-full flex"
    >
      <div className="rounded-xl transition-all duration-200 bg-gradient-to-br from-white/5 via-white/5 to-white/[0.02] backdrop-blur-md hover:from-white/8 hover:via-white/8 hover:to-white/5 hover:scale-[1.02] overflow-hidden shadow-lg shadow-black/20 hover:shadow-xl hover:shadow-black/30 flex flex-col w-full h-[240px]">
        <div className="relative flex-shrink-0">
          <div className="w-full aspect-video rounded-t-lg overflow-hidden bg-gradient-to-br from-purple-500/10 to-indigo-600/10">
            <OptimizedTemplateImage
              src={template.image_url}
              alt={template.title}
              className="w-full h-full object-cover"
            />
          </div>
        </div>
        <div className="px-3 py-2 flex flex-col justify-center min-h-[48px]">
          <h3 className="text-white font-medium text-xs leading-tight text-center line-clamp-2">
            {template.title_i18n?.[locale] || template.title}
          </h3>
        </div>
      </div>
    </Link>
  )
}
